import { defineMessages } from '../define';

export const roles = defineMessages({
  en: {
    'roles.title': 'Roles',
    'roles.description':
      'A role is a named set of permissions. Nothing is inherited: an administrator holds what it is given, not whatever an operator has and more. Accounts take their role from the rank on their Threadline account.',

    'roles.add': 'Add role',
    'roles.create.title': 'New role',
    'roles.create.name': 'Name',
    'roles.create.nameHint': 'Letters, digits, - and _. Used in URLs.',
    'roles.create.label': 'Shown as',
    'roles.column.role': 'Role',
    'roles.column.takes': 'Takes accounts',
    'roles.column.permissions': 'Permissions',
    'roles.column.updated': 'Changed',

    'roles.builtin': 'Built in',
    'roles.yours': 'Yours',
    'roles.takes.nobody': 'Nobody',
    'roles.takes.label': 'Accounts with the role',
    'roles.held': '{count} of {total}',

    'roles.group.monitor': 'Monitor',
    'roles.group.sessions': 'Running sessions',
    'roles.group.automate': 'Automate',
    'roles.group.administration': 'Administer',

    'roles.permission.dashboard.view': 'Overview',
    'roles.permission.sessions.view': 'Sessions',
    'roles.permission.messages.view': 'Inbox',
    'roles.permission.chats.view': 'Chats and transcripts',
    'roles.permission.contacts.view': 'Contacts',
    'roles.permission.sessions.create': 'Create sessions',
    'roles.permission.sessions.update': 'Rename and configure a session',
    'roles.permission.sessions.delete': 'Delete a session',
    'roles.permission.sessions.send': 'Send messages from a session',
    'roles.permission.sessions.backup': 'Back up and restore a session',
    'roles.permission.chats.update': 'Pin, mute and set capture on a chat',
    'roles.permission.messages.viewRaw': 'Read a message as captured',
    'roles.permission.messages.delete': 'Delete a message',
    'roles.permission.logs.clear': 'Clear the logs',
    'roles.permission.export.run': 'Run exports',
    'roles.permission.ai.credentials': 'Hold the AI provider keys',
    'roles.permission.sessions.restart': 'Restart a session',
    'roles.permission.sessions.logout': 'Sign a session out of LINE',
    'roles.permission.webhooks.view': 'Webhooks',
    'roles.permission.webhooks.clear': 'Clear webhook history',
    'roles.permission.keywords.manage': 'Keywords',
    'roles.permission.ai.manage': 'AI',
    'roles.permission.notifications.manage': 'Notifications',
    'roles.permission.banking.view': 'Bank transactions',
    'roles.permission.banking.manage': 'Bank rules',
    'roles.permission.apikeys.manage': 'API keys',
    'roles.permission.logs.view': 'Logs',
    'roles.permission.users.manage': 'Users',
    'roles.permission.audit.view': 'Audit trail',
    'roles.permission.settings.manage': 'Settings',
    'roles.permission.roles.manage': 'Roles',
    'roles.permission.data.viewAll': "See every account's data",
    'roles.permission.data.viewAll.hint':
      'Without this a role sees only what it created. The API enforces this one itself.',

    'roles.notice':
      'These permissions are what the API itself checks. An account may do exactly what the role its rank maps to has been given here - no more, and nothing by virtue of the rank alone.',

    'roles.save': 'Save',
    'roles.saved': 'Saved',
    'roles.deleted': 'Role deleted',
    'roles.created': 'Role created. It holds nothing until you grant something.',
    'roles.delete.confirm': 'Delete this role? Accounts it takes will hold nothing.',
    'roles.delete.builtin': 'A built-in role cannot be deleted.',
    'roles.empty': 'No roles yet.',
  },
  th: {
    'roles.title': 'บทบาท',
    'roles.description':
      'บทบาทคือชุดสิทธิ์ที่ระบุไว้ชัดเจน ไม่มีการสืบทอด ผู้ดูแลระบบมีเท่าที่กำหนดให้ ไม่ได้รับสิทธิ์ของผู้ปฏิบัติงานมาโดยอัตโนมัติ บัญชีจะได้บทบาทตามระดับสิทธิ์ในบัญชี Threadline ของตน',

    'roles.add': 'เพิ่มบทบาท',
    'roles.create.title': 'บทบาทใหม่',
    'roles.create.name': 'ชื่อ',
    'roles.create.nameHint': 'ตัวอักษร ตัวเลข - และ _ ใช้ใน URL',
    'roles.create.label': 'แสดงเป็น',
    'roles.column.role': 'บทบาท',
    'roles.column.takes': 'รับบัญชีจาก',
    'roles.column.permissions': 'สิทธิ์',
    'roles.column.updated': 'แก้ไขเมื่อ',

    'roles.builtin': 'มากับระบบ',
    'roles.yours': 'ของคุณ',
    'roles.takes.nobody': 'ไม่รับใคร',
    'roles.takes.label': 'บัญชีที่มีระดับสิทธิ์',
    'roles.held': '{count} จาก {total}',

    'roles.group.monitor': 'เฝ้าดู',
    'roles.group.sessions': 'ควบคุมเซสชัน',
    'roles.group.automate': 'ทำงานอัตโนมัติ',
    'roles.group.administration': 'ดูแลระบบ',

    'roles.permission.dashboard.view': 'ภาพรวม',
    'roles.permission.sessions.view': 'เซสชัน',
    'roles.permission.messages.view': 'กล่องข้อความ',
    'roles.permission.chats.view': 'แชทและข้อความย้อนหลัง',
    'roles.permission.contacts.view': 'รายชื่อผู้ติดต่อ',
    'roles.permission.sessions.create': 'สร้างเซสชัน',
    'roles.permission.sessions.update': 'แก้ชื่อและตั้งค่าเซสชัน',
    'roles.permission.sessions.delete': 'ลบเซสชัน',
    'roles.permission.sessions.send': 'ส่งข้อความจากเซสชัน',
    'roles.permission.sessions.backup': 'สำรองและกู้คืนเซสชัน',
    'roles.permission.chats.update': 'ปักหมุด ปิดเสียง และเปิดการเก็บข้อมูลแชท',
    'roles.permission.messages.viewRaw': 'ดูข้อความดิบที่เก็บมา',
    'roles.permission.messages.delete': 'ลบข้อความ',
    'roles.permission.logs.clear': 'ล้างบันทึกระบบ',
    'roles.permission.export.run': 'สั่งส่งออกข้อมูล',
    'roles.permission.ai.credentials': 'ถือคีย์ผู้ให้บริการ AI',
    'roles.permission.sessions.restart': 'รีสตาร์ตเซสชัน',
    'roles.permission.sessions.logout': 'ออกจากระบบ LINE ของเซสชัน',
    'roles.permission.webhooks.view': 'เว็บฮุก',
    'roles.permission.webhooks.clear': 'ล้างประวัติเว็บฮุก',
    'roles.permission.keywords.manage': 'คำสำคัญ',
    'roles.permission.ai.manage': 'AI',
    'roles.permission.notifications.manage': 'การแจ้งเตือน',
    'roles.permission.banking.view': 'รายการเดินบัญชี',
    'roles.permission.banking.manage': 'กฎธนาคาร',
    'roles.permission.apikeys.manage': 'คีย์ API',
    'roles.permission.logs.view': 'บันทึกระบบ',
    'roles.permission.users.manage': 'ผู้ใช้',
    'roles.permission.audit.view': 'ร่องรอยการตรวจสอบ',
    'roles.permission.settings.manage': 'ตั้งค่า',
    'roles.permission.roles.manage': 'บทบาท',
    'roles.permission.data.viewAll': 'เห็นข้อมูลของทุกบัญชี',
    'roles.permission.data.viewAll.hint':
      'ถ้าไม่มีสิทธิ์นี้ บทบาทจะเห็นเฉพาะสิ่งที่ตัวเองสร้าง สิทธิ์นี้ API เป็นผู้บังคับใช้เอง',

    'roles.notice':
      'สิทธิ์เหล่านี้คือสิ่งที่ API ตรวจเอง บัญชีหนึ่งทำได้เท่าที่บทบาทของตนถูกกำหนดไว้ที่นี่ ไม่มีสิทธิ์ใดได้มาเพราะระดับบัญชีเพียงอย่างเดียว',

    'roles.save': 'บันทึก',
    'roles.saved': 'บันทึกแล้ว',
    'roles.deleted': 'ลบบทบาทแล้ว',
    'roles.created': 'สร้างบทบาทแล้ว ยังไม่มีสิทธิ์ใดจนกว่าจะกำหนดให้',
    'roles.delete.confirm': 'ลบบทบาทนี้หรือไม่ บัญชีที่รับจากบทบาทนี้จะไม่มีสิทธิ์ใดเลย',
    'roles.delete.builtin': 'บทบาทที่มากับระบบลบไม่ได้',
    'roles.empty': 'ยังไม่มีบทบาท',
  },
});
